/**
 * Tutor information-session detail tab.
 * Fetches the linked session, shows participation, and can mark / cancel it.
 */
import * as React from 'react';
import { UserCheck, XCircle, CheckCircle2, Eye, FileEdit } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LabelBadge } from '@/components/common/LabelBadge';
import { DetailsCard } from '@/components/common/DetailsCard';
import { DetailField } from '@/components/common/details/DetailField';
import { Button } from '@/components/forms/Button';
import { useMarkTutorInformationSessionParticipation } from '@/hooks/useUsers';
import { useNavigateToRemoteReport } from '@/components/sessions/remote/session-table-utils';
import { useAuthStore } from '@/stores/authStore';
import { getSession } from '@/services/remote/sessions.service';
import { formatDate } from '@/components/tutors/remote/tutor-table-utils';

/** Props for {@link TutorInfoSessionTab}. */
interface TutorInfoSessionTabProps {
  userId: string;
  sections: Record<string, unknown>;
  onEditSection?: (stepKey: string) => void;
}

/** Tutor information-session detail tab. */
export const TutorInfoSessionTab: React.FC<TutorInfoSessionTabProps> = ({
  userId,
  sections,
  onEditSection,
}) => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { navigateToReport, isLoading: isLoadingReportNav } = useNavigateToRemoteReport();
  const informationMeetingMutation = useMarkTutorInformationSessionParticipation();

  const informationMeeting = sections['information_session'] as any;
  const informationMeetingSessionId = informationMeeting?.sessionId;

  const { data: informationMeetingSession, isLoading: isLoadingInformationMeetingSession } =
    useQuery({
      queryKey: ['remote-session', informationMeetingSessionId],
      queryFn: () => getSession(informationMeetingSessionId!),
      enabled: !!informationMeetingSessionId,
    });

  const handleMarkRI = () => {
    if (!user?.companyId) return;
    informationMeetingMutation.mutate({
      userId,
      companyId: user.companyId,
      participated: !informationMeeting?.hasParticipated,
    });
  };

  return (
    <div className="details-view-grid mt-6">
      <DetailsCard icon={UserCheck} title="Réunion d'information" colorName="color-3" colSpan={2}>
        {!informationMeeting ? (
          <p className="text-sm text-muted-foreground">Aucune donnée de réunion d'information.</p>
        ) : (
          (() => {
            const markedByName =
              informationMeeting.markedBy?.userId === userId
                ? "S'est marqué lui-même"
                : `${informationMeeting.markedBy?.firstName || ''} ${informationMeeting.markedBy?.lastName || ''}`.trim() ||
                  '—';
            return (
              <div className="space-y-3">
                <DetailField
                  label="Participation"
                  value={
                    <LabelBadge
                      label={informationMeeting.hasParticipated ? 'Participé' : 'Non participé'}
                      icon={informationMeeting.hasParticipated ? CheckCircle2 : XCircle}
                      variant="outlined"
                      color={
                        informationMeeting.hasParticipated
                          ? 'rgb(var(--color-success))'
                          : 'rgb(var(--color-warning))'
                      }
                      size="sm"
                    />
                  }
                />
                {informationMeeting.hasParticipated && informationMeeting.markedBy ? (
                  <DetailField label="Marqué par" value={markedByName} />
                ) : (
                  <>
                    {informationMeeting.startDate && (
                      <DetailField
                        label="Planifiée le"
                        value={formatDate(informationMeeting.startDate)}
                      />
                    )}
                    {informationMeeting.hasParticipated && informationMeeting.participatedAt && (
                      <DetailField
                        label="Date de participation"
                        value={formatDate(informationMeeting.participatedAt)}
                      />
                    )}
                    {informationMeeting.hasParticipated && informationMeeting.hourin && (
                      <DetailField label="Connexion" value={informationMeeting.hourin} />
                    )}
                    {informationMeeting.hasParticipated && informationMeeting.hourout && (
                      <DetailField label="Déconnexion" value={informationMeeting.hourout} />
                    )}
                  </>
                )}
              </div>
            );
          })()
        )}
      </DetailsCard>

      {onEditSection && (
        <div className="section-card-full flex gap-3">
          {(() => {
            const hasParticipated = informationMeeting?.hasParticipated === true;
            return (
              <Button
                size="sm"
                variant="outline"
                icon={hasParticipated ? XCircle : UserCheck}
                loading={informationMeetingMutation.isPending}
                onClick={handleMarkRI}
              >
                {hasParticipated ? 'Annuler la participation' : 'Marquer comme participant'}
              </Button>
            );
          })()}

          {informationMeetingSessionId &&
            informationMeetingSession?.status === 'COMPLETED' &&
            (() => {
              const reportValidated = ['valide', 'complet'].includes(
                (informationMeetingSession as any)?.compteRenduStatus ?? '',
              );
              return (
                <Button
                  size="sm"
                  variant="outline"
                  icon={reportValidated ? Eye : FileEdit}
                  loading={isLoadingInformationMeetingSession || isLoadingReportNav}
                  onClick={() => {
                    if (reportValidated && (informationMeetingSession as any).compteRenduId) {
                      void navigate('/reports', {
                        state: {
                          viewReport: {
                            reportId: (informationMeetingSession as any).compteRenduId,
                          },
                        },
                      });
                    } else {
                      void navigateToReport(informationMeetingSessionId);
                    }
                  }}
                >
                  {reportValidated ? 'Voir le compte rendu' : 'Saisir / modifier le compte rendu'}
                </Button>
              );
            })()}
        </div>
      )}
    </div>
  );
};
